/* Options → Watchlist sub-tab. CRUD table over /api/trading/options_watchlist. */

import { useState } from "react";
import { C, Pill, Badge } from "./_shared";
import { useCompanyNames } from "./_companyNames";
import { STRATEGIES, BIASES, LIQUIDITIES, SETUP_STATUSES, apiJSON, F, S } from "./_optionsShared";

export default function OptionsWatchlist({ watchlist, refreshWatchlist, onAnalyse }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(null);
  const { name: companyName } = useCompanyNames();

  const blank = () => ({ ticker: "", current_price: "", dma_20: "", dma_50: "", trend: "Bullish", support: "", resistance: "", trigger_price: "", invalidation: "", preferred_strategy: "Call Debit Spread", option_liquidity: "Tight", setup_status: "Watching", notes: "" });
  async function del(id) {
    if (!window.confirm("Remove from watchlist?")) return;
    try { await apiJSON(`/api/trading/options_watchlist/${id}`, { method: "DELETE" }); refreshWatchlist(); } catch { /* noop */ }
  }
  async function save() {
    try {
      const body = { ...form, ticker: form.ticker.toUpperCase(),
        current_price: +form.current_price || 0, dma_20: +form.dma_20 || 0, dma_50: +form.dma_50 || 0,
        support: +form.support || 0, resistance: +form.resistance || 0,
        trigger_price: +form.trigger_price || 0, invalidation: +form.invalidation || 0 };
      if (form.id) await apiJSON(`/api/trading/options_watchlist/${form.id}`, { method: "PUT", body: JSON.stringify(body) });
      else         await apiJSON(`/api/trading/options_watchlist`, { method: "POST", body: JSON.stringify(body) });
      setAdding(false); setForm(null); refreshWatchlist();
    } catch (e) { alert("Save failed: " + e.message); }
  }

  const statusStyle = { Watching: {bg: C.ab, text: C.at}, Waiting: {bg: C.ib, text: C.it}, Ready: {bg: C.gb, text: C.gt}, "No Trade": {bg: C.rb, text: C.rt} };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <button onClick={() => { setAdding(true); setForm(blank()); }} data-testid="options-watch-add-btn"
          style={{ padding: "7px 14px", borderRadius: 7, background: C.indigo, color: "#fff", border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>+ Add Watchlist Ticker</button>
        <span style={{ fontSize: 11, color: C.gray }}>{watchlist.length} tickers tracked</span>
      </div>

      {adding && form && (
        <div style={{ background: "#fff", border: `1px solid ${C.indigo}44`, borderRadius: 10, padding: "12px 14px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 8 }}>
            {F({ label: "Ticker", value: form.ticker, onChange: v => setForm(f => ({ ...f, ticker: v })), placeholder: "MSFT" })}
            {F({ label: "Current Price", value: form.current_price, onChange: v => setForm(f => ({ ...f, current_price: v })), type: "number", placeholder: "auto" })}
            {F({ label: "20 DMA", value: form.dma_20, onChange: v => setForm(f => ({ ...f, dma_20: v })), type: "number" })}
            {F({ label: "50 DMA", value: form.dma_50, onChange: v => setForm(f => ({ ...f, dma_50: v })), type: "number" })}
            {S({ label: "Trend", value: form.trend, onChange: v => setForm(f => ({ ...f, trend: v })), options: BIASES })}
            {F({ label: "Support", value: form.support, onChange: v => setForm(f => ({ ...f, support: v })), type: "number" })}
            {F({ label: "Resistance", value: form.resistance, onChange: v => setForm(f => ({ ...f, resistance: v })), type: "number" })}
            {F({ label: "Trigger Price", value: form.trigger_price, onChange: v => setForm(f => ({ ...f, trigger_price: v })), type: "number" })}
            {F({ label: "Invalidation", value: form.invalidation, onChange: v => setForm(f => ({ ...f, invalidation: v })), type: "number" })}
            {S({ label: "Preferred Strategy", value: form.preferred_strategy, onChange: v => setForm(f => ({ ...f, preferred_strategy: v })), options: STRATEGIES })}
            {S({ label: "Option Liquidity", value: form.option_liquidity, onChange: v => setForm(f => ({ ...f, option_liquidity: v })), options: LIQUIDITIES })}
            {S({ label: "Setup Status", value: form.setup_status, onChange: v => setForm(f => ({ ...f, setup_status: v })), options: SETUP_STATUSES })}
          </div>
          <div style={{ marginTop: 8 }}>
            {F({ label: "Notes", value: form.notes, onChange: v => setForm(f => ({ ...f, notes: v })) })}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button onClick={save} data-testid="options-watch-save-btn"
              style={{ padding: "7px 16px", borderRadius: 7, background: C.indigo, color: "#fff", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Save</button>
            <button onClick={() => { setAdding(false); setForm(null); }}
              style={{ padding: "7px 12px", borderRadius: 7, background: C.gbg, color: C.gray, border: "none", fontSize: 12, cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
        {watchlist.length === 0 ? (
          <div data-testid="options-watch-empty" style={{ padding: "32px 16px", textAlign: "center", fontSize: 12, color: C.gray }}>
            No tickers on your options watchlist. Click + Add to start tracking setups.
          </div>
        ) : (
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, minWidth: 1100 }}>
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  {["Ticker","Current","20 DMA","50 DMA","Trend","Support","Resistance","Trigger","Invalidation","Strategy","Liquidity","Status","Notes",""].map(h => (
                    <th key={h} style={{ padding: "6px 8px", textAlign: "left", color: C.gray, fontWeight: 500, fontSize: 10, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {watchlist.map(w => {
                  const st = statusStyle[w.setup_status] || { bg: C.gbg, text: C.gt2 };
                  return (
                    <tr key={w.id} data-testid={`options-watch-${w.ticker}`} style={{ borderTop: "0.5px solid #f3f4f6" }}>
                      <td style={{ padding: "6px 8px" }}>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <span onClick={() => onAnalyse(w.ticker)} style={{ fontWeight: 800, color: C.indigo, cursor: "pointer", textDecoration: "underline" }}>{w.ticker}</span>
                          <span style={{ fontSize: 9, color: C.muted, whiteSpace: "nowrap", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis" }}>{companyName(w.ticker, "")}</span>
                        </div>
                      </td>
                      <td style={{ padding: "6px 8px", fontWeight: 700, color: "#111" }}>${(+w.current_price || 0).toFixed(2)}</td>
                      <td style={{ padding: "6px 8px", color: C.gray }}>${(+w.dma_20 || 0).toFixed(2)}</td>
                      <td style={{ padding: "6px 8px", color: C.gray }}>${(+w.dma_50 || 0).toFixed(2)}</td>
                      <td style={{ padding: "6px 8px" }}><Pill label={w.trend} bg={w.trend === "Bullish" ? C.gb : w.trend === "Bearish" ? C.rb : C.gbg} text={w.trend === "Bullish" ? C.gt : w.trend === "Bearish" ? C.rt : C.gt2}/></td>
                      <td style={{ padding: "6px 8px", color: C.gray }}>${(+w.support || 0).toFixed(2)}</td>
                      <td style={{ padding: "6px 8px", color: C.gray }}>${(+w.resistance || 0).toFixed(2)}</td>
                      <td style={{ padding: "6px 8px", color: C.green, fontWeight: 600 }}>${(+w.trigger_price || 0).toFixed(2)}</td>
                      <td style={{ padding: "6px 8px", color: C.red, fontWeight: 600 }}>${(+w.invalidation || 0).toFixed(2)}</td>
                      <td style={{ padding: "6px 8px", fontSize: 10, color: C.indigo, fontWeight: 500 }}>{w.preferred_strategy}</td>
                      <td style={{ padding: "6px 8px", color: C.gray }}>{w.option_liquidity}</td>
                      <td style={{ padding: "6px 8px" }}><Badge label={w.setup_status} bg={st.bg} text={st.text} size={10}/></td>
                      <td style={{ padding: "6px 8px", color: C.gray, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{w.notes}</td>
                      <td style={{ padding: "6px 8px" }}>
                        <div style={{ display: "flex", gap: 4 }}>
                          <button onClick={() => { setForm({ ...w }); setAdding(true); }}
                            style={{ fontSize: 10, padding: "2px 7px", borderRadius: 5, border: "0.5px solid #e5e7eb", background: "#f9fafb", cursor: "pointer", color: C.gray }}>Edit</button>
                          <button onClick={() => del(w.id)}
                            style={{ fontSize: 10, padding: "2px 7px", borderRadius: 5, border: "none", background: C.rb, cursor: "pointer", color: C.rt }}>Del</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
